/**
 * Widget action dispatcher — assembles the per-widget processor factories into
 * a single `WidgetActionProcessor[]` and exposes `initializeWidgetProcessorMap`
 * to the widget store.
 *
 * Lives in the APP layer (`app/store/widget/`) because:
 *   - It value-imports concrete processor factories from every per-widget
 *     feature slice (cross-feature OUT — only legal from `app/`/`pages/`).
 *   - It binds the entity-layer `MakeCreateWidgetActionDeps` contract to the
 *     concrete `widgetProcessorDeps` (chat store, UI store, agent-title
 *     resolver) defined in `./widget-action.deps`.
 *   - The widget store at `./widget.store` is its sole consumer.
 *
 * Relocated from `features/widgets/processors/index.ts`. The features-layer
 * location no longer made sense once every widget owned its own slice.
 */
import { WidgetSetter, WidgetWithMeta } from '@/app/store';
import type { WidgetActionProcessor } from '@/entities/widget';
// EmailFlow heavily value-imports `@/app/store/widget` (`useWidgetPanel`,
// `useWidgetStore`) and `@/app/store`; going through the slice public
// barrel creates a circular dependency that breaks at module-init time.
// Import the factory via DEEP PATH (model/email-flow-widget.processor) —
// INTENTIONAL allow-list exception for the dispatcher only.
import { makeProcessors as makeEmailFlowProcessors } from '@/features/email-flow-widget/model/email-flow-widget.processor';
// TemplatesAgent itself does NOT value-import `@/app/store/widget` nor
// `@/app/store` (the wrapper is purely a render delegation that injects
// EmailFlow as a prop), so the deep-path workaround is NOT functionally
// required and this import goes through the public barrel — the
// deep-import ban is ENFORCED for templates-agent-widget per its
// `canonicalLeafFeaturesConfig` block.
import { makeProcessors as makeTemplatesAgentProcessors } from '@/features/templates-agent-widget';
// SocialBriefWidget value-imports `@/app/store/widget` (`useWidgetPanel`);
// going through the slice public barrel creates a circular dependency
// that breaks at module-init time. Import the factory via DEEP PATH
// (model/social-brief-widget.processor) — INTENTIONAL allow-list
// exception for the dispatcher only.
import { makeProcessors as makeSocialBriefProcessors } from '@/features/social-brief-widget/model/social-brief-widget.processor';

// AudienceFlowWidget + BehindTheAnswerWidget do NOT value-import the
// widget store (`@/app/store/widget` or `@/app/store`), so the deep-path
// workaround is NOT required. Importing both factories via deep path
// anyway for consistency with sibling decomposed widgets and as a
// defensive measure against future cycles.
import { makeProcessors as makeAudienceFlowProcessors } from '@/features/audience-flow-widget/model/audience-flow-widget.processor';
// SocialMediaOpportunities does NOT value-import from `@/app/store/widget`
// nor `@/app/store` (only `@/app/store/ui`), so the deep-path workaround
// is NOT required. Importing the factory via deep path anyway for
// consistency with sibling decomposed widgets and as a defensive measure
// against future cycles.
import { makeProcessors as makeSocialMediaOpportunitiesProcessors } from '@/features/social-media-opportunities-widget/model/social-media-opportunities-widget.processor';
// Import processor factories via DEEP PATH (model/<x>.processor) — NOT via the
// slice public barrel. The widget UI components transitively import from
// `@/app/store/widget`, which itself loads this dispatcher → going through the
// public barrel creates a circular dependency that breaks at module-init time.
// The deep-path import touches ONLY the processor factory module (no React
// component imports), avoiding the cycle. This is an INTENTIONAL allow-list
// EXCEPTION for the dispatcher only — every other consumer MUST go through the
// public barrel (enforced by ESLint + per-slice public-api.test snapshots).
import { makeProcessors as makeLoremIpsumProcessors } from '@/features/lorem-ipsum-widget/model/lorem-ipsum-widget.processor';
import { widgetProcessorDeps } from './widget-action.deps';
import { makeProcessors as makeBehindTheAnswerProcessors } from '@/features/audience-flow-widget/model/behind-the-answer.processor';
import { makeProcessors as makeStrategicTerritoriesProcessors } from '@/features/strategic-territories-widget/model/strategic-territories-widget.processor';
import { makeProcessors as makeFourCsProcessors } from '@/features/four-cs-widget/model/four-cs-widget.processor';
import { makeProcessors as makeCopyProcessors } from '@/features/copy-widget/model/copy-widget.processor';
import { makeProcessors as makeImageProcessors } from '@/features/image-widget/model/image-widget.processor';
import { makeProcessors as makeVideoProcessors } from '@/features/video-widget/model/video-widget.processor';
import { makeProcessors as makeIdeationProcessors } from '@/features/ideation-widget/model/ideation-widget.processor';
import { makeProcessors as makeHtmlWidgetProcessors } from '@/features/html-widget/model/html-widget.processor';

const WIDGET_PROCESSORS = [
  ...makeEmailFlowProcessors(widgetProcessorDeps),
  ...makeTemplatesAgentProcessors(widgetProcessorDeps),
  ...makeSocialBriefProcessors(widgetProcessorDeps),
  ...makeSocialMediaOpportunitiesProcessors(widgetProcessorDeps),
  ...makeImageProcessors(widgetProcessorDeps),
  ...makeCopyProcessors(widgetProcessorDeps),
  ...makeVideoProcessors(widgetProcessorDeps),
  ...makeLoremIpsumProcessors(widgetProcessorDeps),
  ...makeAudienceFlowProcessors(widgetProcessorDeps),
  ...makeBehindTheAnswerProcessors(widgetProcessorDeps),
  ...makeFourCsProcessors(widgetProcessorDeps),
  ...makeStrategicTerritoriesProcessors(widgetProcessorDeps),
  ...makeIdeationProcessors(widgetProcessorDeps),
  ...makeHtmlWidgetProcessors(widgetProcessorDeps),
];

export const initializeWidgetProcessorMap = (
  set: WidgetSetter,
): Map<string, (messageId: string, widget: WidgetWithMeta) => void> => {
  const processorMap = new Map<string, ReturnType<WidgetActionProcessor['processor']>>();
  WIDGET_PROCESSORS.forEach(({ key, processor }) => {
    if (processorMap.has(key)) throw new Error(`Duplicate widget processor: ${key}`);
    processorMap.set(key, processor(set));
  });

  return processorMap;
};
